"use client";

;
!function () {
  try {
    var e = "undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof globalThis ? globalThis : "undefined" != typeof self ? self : {};
    e._sentryModuleMetadata = e._sentryModuleMetadata || {}, e._sentryModuleMetadata[new e.Error().stack] = Object.assign({}, e._sentryModuleMetadata[new e.Error().stack], {
      "_sentryBundlerPluginAppKey:propstrata-app": true
    });
  } catch (e) {}
}();
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Controls, Step } from "react-joyride";
import { buildTourSteps } from "@/features/onboarding-tours/orchestrator/build-tour-steps";
import { isTourLaunchPathActive } from "@/features/onboarding-tours/orchestrator/launch-tour";
import { runTourStepPrep } from "@/features/onboarding-tours/orchestrator/run-tour-step-prep";
import { createTourJoyrideEventHandler } from "@/features/onboarding-tours/orchestrator/tour-joyride-events";
import { buildStartTourAction, canStartTour, recordTourEnd, resolveHandoffAction, resolvePipelineAdvanceAction, type StartTourRequest, shouldAttemptPipeline, type TourOrchestratorAction } from "@/features/onboarding-tours/orchestrator/tour-orchestrator";
import { closeTourOverlayGroup, resolveTourAdvanceOn, TOUR_OVERLAY_GROUPS } from "@/features/onboarding-tours/orchestrator/tour-overlay-groups";
import { writeOnboardingToursStorage } from "@/features/onboarding-tours/orchestrator/tour-persistence";
import { closeCreateDataProviderDialog, closeInviteMembersSheet } from "@/features/onboarding-tours/orchestrator/tour-step-actions";
import { isTourTargetVisible } from "@/features/onboarding-tours/orchestrator/wait-for-tour-target";
import type { TourEligibilityContext, TourEndReason, TourId, TourStepBuildContext, TourStepData } from "@/features/onboarding-tours/types/tour-definition";
import type { OnboardingToursStorageValue } from "@/features/onboarding-tours/types/tour-persistence";
export interface UseTourSessionParams {
  workspaceId: string | undefined;
  organizationId: string | undefined;
  userId: string | undefined;
  storage: OnboardingToursStorageValue;
  setStorage: React.Dispatch<React.SetStateAction<OnboardingToursStorageValue>>;
  isVisualizationGuardActive: boolean;
  eligibilityContext: TourEligibilityContext;
  activeViewNames?: string[];
}
function getStepData({
  step
}: {
  step: Step | undefined;
}): TourStepData {
  return (step?.data ?? {}) as TourStepData;
}
export function useTourSession({
  workspaceId,
  organizationId,
  userId,
  storage,
  setStorage,
  isVisualizationGuardActive,
  eligibilityContext,
  activeViewNames = []
}: UseTourSessionParams) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentSearch = searchParams.toString();
  const [activeTourId, setActiveTourId] = useState<TourId | null>(null);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [framingByTourId, setFramingByTourId] = useState<Partial<Record<TourId, TourStepBuildContext["framing"]>>>({});
  const [pendingLaunch, setPendingLaunch] = useState<TourOrchestratorAction | null>(null);
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);
  const [shareViewTourUnavailableOpen, setShareViewTourUnavailableOpen] = useState(false);
  const joyrideControlsRef = useRef<Controls | null>(null);
  const advancedForStepRef = useRef<number | null>(null);
  const setTourStepIndex = useCallback((nextIndex: number) => {
    advancedForStepRef.current = null;
    setStepIndex(nextIndex);
  }, []);
  const ensureRoute = useCallback(async ({
    route
  }: {
    route: string;
  }) => {
    const isActive = isTourLaunchPathActive({
      currentPathname: pathname,
      currentSearch,
      targetRoute: route
    });
    if (isActive) {
      return;
    }
    setPendingRoute(route);
    router.push(route);
    const deadline = Date.now() + 5000;
    while (Date.now() < deadline) {
      await new Promise<void>(resolve => {
        window.setTimeout(resolve, 100);
      });
    }
  }, [currentSearch, pathname, router]);
  const ensureRouteRef = useRef(ensureRoute);
  ensureRouteRef.current = ensureRoute;
  const beginTourRun = useCallback(({
    tourId
  }: {
    tourId: TourId;
  }) => {
    setActiveTourId(tourId);
    setTourStepIndex(0);
    setRun(true);
  }, [setTourStepIndex]);
  const stopTourRun = useCallback(() => {
    setRun(false);
    setTourStepIndex(0);
    setActiveTourId(null);
  }, [setTourStepIndex]);

  /** Freeze step identities for the active tour so Joyride does not remount mid-run. */
  const steps = useMemo((): Step[] => {
    if (!workspaceId || !activeTourId) {
      return [];
    }
    return buildTourSteps({
      currentPathname: pathname,
      currentSearch,
      ensureRoute: ({
        route: route_0
      }) => ensureRouteRef.current({
        route: route_0
      }),
      framing: framingByTourId[activeTourId],
      tourId: activeTourId,
      workspaceId
    });
  }, [activeTourId, currentSearch, framingByTourId, pathname, workspaceId]);
  useEffect(function prepareActiveTourStep() {
    if (!run || !activeTourId) {
      return;
    }
    const abortController = new AbortController();
    void runTourStepPrep({
      signal: abortController.signal,
      step: steps[stepIndex]
    });
    return () => {
      abortController.abort();
    };
  }, [activeTourId, run, stepIndex, steps]);
  const advanceToTourStep = useCallback(({
    fromStepIndex,
    toStepIndex
  }: {
    fromStepIndex: number;
    toStepIndex: number;
  }) => {
    if (advancedForStepRef.current === fromStepIndex) {
      return;
    }
    if (fromStepIndex !== stepIndex) {
      return;
    }
    advancedForStepRef.current = fromStepIndex;
    const fromGroup = getStepData({
      step: steps[fromStepIndex]
    }).overlayGroup;
    const toGroup = getStepData({
      step: steps[toStepIndex]
    }).overlayGroup;
    if (fromGroup && fromGroup !== toGroup) {
      closeTourOverlayGroup({
        overlayGroup: fromGroup
      });
    }
    joyrideControlsRef.current?.go(toStepIndex);
    setTourStepIndex(toStepIndex);
  }, [setTourStepIndex, stepIndex, steps]);
  const advanceFromUserAction = useCallback(({
    forStepIndex
  }: {
    forStepIndex: number;
  }) => {
    if (advancedForStepRef.current === forStepIndex) {
      return;
    }
    advancedForStepRef.current = forStepIndex;
    const fromGroup_0 = getStepData({
      step: steps[forStepIndex]
    }).overlayGroup;
    const toGroup_0 = getStepData({
      step: steps[forStepIndex + 1]
    }).overlayGroup;
    if (fromGroup_0 && fromGroup_0 !== toGroup_0) {
      closeTourOverlayGroup({
        overlayGroup: fromGroup_0
      });
    }
    joyrideControlsRef.current?.next();
    setTourStepIndex(forStepIndex + 1);
  }, [setTourStepIndex, steps]);
  useEffect(function advanceTourWhenTargetVisible() {
    if (!run || !activeTourId) {
      return;
    }
    const currentStep = steps[stepIndex];
    const stepData = getStepData({
      step: currentStep
    });
    const advanceOn = resolveTourAdvanceOn({
      advanceOn: stepData.advanceOn,
      advanceWhenTarget: stepData.advanceWhenTarget
    });
    if (advanceOn !== "target-visible" || !stepData.advanceWhenTarget) {
      return;
    }
    const watchTarget = stepData.advanceWhenTarget;
    const intervalId = window.setInterval(() => {
      if (!isTourTargetVisible({
        target: watchTarget
      })) {
        return;
      }
      advanceFromUserAction({
        forStepIndex: stepIndex
      });
      window.clearInterval(intervalId);
    }, 100);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [activeTourId, advanceFromUserAction, run, stepIndex, steps]);
  useEffect(function advanceTourWhenWatchedTargetClicked() {
    if (!run || !activeTourId) {
      return;
    }
    const stepData_0 = getStepData({
      step: steps[stepIndex]
    });
    const watchTargetClick = stepData_0.watchTargetClick;
    if (!watchTargetClick) {
      return;
    }
    const watchSelector = watchTargetClick.selector;
    const targetStepIndex = watchTargetClick.stepIndex;
    function handleDocumentClick(event: MouseEvent) {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Element)) {
        return;
      }
      if (!eventTarget.closest(watchSelector)) {
        return;
      }
      const fromStepIndex_0 = stepIndex;
      // Let the host surface mount (Credenza/AlertDialog) before Joyride's step hook runs.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          advanceToTourStep({
            fromStepIndex: fromStepIndex_0,
            toStepIndex: targetStepIndex
          });
        });
      });
    }
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [activeTourId, advanceToTourStep, run, stepIndex, steps]);
  const launchTourAction = useCallback((action: TourOrchestratorAction) => {
    if (action.type === "unavailable") {
      if (action.tourId === "share-a-view") {
        setShareViewTourUnavailableOpen(true);
      }
      return;
    }
    if (action.framing) {
      setFramingByTourId(previous => ({
        ...previous,
        [action.tourId]: action.framing
      }));
    }
    if (action.type === "start") {
      setPendingLaunch(null);
      beginTourRun({
        tourId: action.tourId
      });
      return;
    }
    const route_1 = action.route;
    if (!route_1) {
      setPendingLaunch(null);
      beginTourRun({
        tourId: action.tourId
      });
      return;
    }
    const isActive_0 = isTourLaunchPathActive({
      currentPathname: pathname,
      currentSearch,
      targetRoute: route_1
    });
    if (!isActive_0) {
      setPendingLaunch(action);
      router.push(route_1);
      return;
    }
    setPendingLaunch(null);
    beginTourRun({
      tourId: action.tourId
    });
  }, [beginTourRun, currentSearch, pathname, router]);
  const executeLaunch = useCallback((action_0: TourOrchestratorAction) => {
    if (!canStartTour({
      isTourActive: run && activeTourId !== null
    })) {
      return;
    }
    launchTourAction(action_0);
  }, [activeTourId, launchTourAction, run]);
  useEffect(function resumePendingTourLaunch() {
    if (!pendingLaunch) {
      return;
    }
    const route_2 = pendingLaunch.route;
    if (!route_2) {
      const launchAction = pendingLaunch;
      setPendingLaunch(null);
      beginTourRun({
        tourId: launchAction.tourId
      });
      return;
    }
    const isActive_1 = isTourLaunchPathActive({
      currentPathname: pathname,
      currentSearch,
      targetRoute: route_2
    });
    if (!isActive_1) {
      return;
    }
    const launchAction_0 = pendingLaunch;
    setPendingLaunch(null);
    beginTourRun({
      tourId: launchAction_0.tourId
    });
  }, [beginTourRun, currentSearch, pathname, pendingLaunch]);
  useEffect(function clearPendingRouteWhenActive() {
    if (!pendingRoute) {
      return;
    }
    const isActive_2 = isTourLaunchPathActive({
      currentPathname: pathname,
      currentSearch,
      targetRoute: pendingRoute
    });
    if (isActive_2) {
      setPendingRoute(null);
    }
  }, [currentSearch, pathname, pendingRoute]);
  const closeActiveOverlayGroup = useCallback(({
    reason
  }: {
    reason: TourEndReason;
  }) => {
    const overlayGroup = getStepData({
      step: steps[stepIndex]
    }).overlayGroup;
    if (!overlayGroup) {
      return;
    }
    const keepOpenOnComplete = reason === "complete" && (overlayGroup === TOUR_OVERLAY_GROUPS.createDataProvider || overlayGroup === TOUR_OVERLAY_GROUPS.leaderboardConfig);
    if (keepOpenOnComplete) {
      return;
    }
    closeTourOverlayGroup({
      overlayGroup
    });
  }, [stepIndex, steps]);
  const finalizeTourEnd = useCallback(({
    tourId: tourId_0,
    reason: reason_0
  }: {
    tourId: TourId;
    reason: TourEndReason;
  }) => {
    if (!organizationId || !userId || !workspaceId) {
      return;
    }
    closeActiveOverlayGroup({
      reason: reason_0
    });
    stopTourRun();
    const nextStorage = recordTourEnd({
      reason: reason_0,
      storage,
      tourId: tourId_0
    });
    writeOnboardingToursStorage({
      organizationId,
      userId,
      value: nextStorage
    });
    setStorage(nextStorage);
    if (reason_0 === "complete") {
      const handoff = resolveHandoffAction({
        completedTourId: tourId_0,
        eligibilityContext,
        workspaceId
      });
      if (handoff) {
        launchTourAction(handoff);
        return;
      }
    }
    if (!shouldAttemptPipeline({
      isTourActive: false
    })) {
      return;
    }
    const advance = resolvePipelineAdvanceAction({
      pipelineContext: {
        canInvite: eligibilityContext.canInvite,
        isVisualizationGuardActive
      },
      storage: nextStorage,
      workspaceId
    });
    if (advance) {
      launchTourAction(advance);
    }
  }, [closeActiveOverlayGroup, eligibilityContext, isVisualizationGuardActive, launchTourAction, organizationId, setStorage, stopTourRun, storage, userId, workspaceId]);
  useEffect(function advanceTourWhenTargetClicked() {
    if (!run || !activeTourId) {
      return;
    }
    const currentStep_0 = steps[stepIndex];
    const stepData_1 = getStepData({
      step: currentStep_0
    });
    const advanceOn_0 = resolveTourAdvanceOn({
      advanceOn: stepData_1.advanceOn,
      advanceWhenTarget: stepData_1.advanceWhenTarget
    });
    if (advanceOn_0 !== "target-click") {
      return;
    }
    const target = currentStep_0?.target;
    if (typeof target !== "string") {
      return;
    }
    const targetSelector = target;
    const forStepIndex_0 = stepIndex;
    function handleDocumentClick_0(event_0: MouseEvent) {
      const eventTarget_0 = event_0.target;
      if (!(eventTarget_0 instanceof Element)) {
        return;
      }
      const matched = eventTarget_0.closest(targetSelector);
      if (!matched) {
        return;
      }
      advanceFromUserAction({
        forStepIndex: forStepIndex_0
      });
    }
    document.addEventListener("click", handleDocumentClick_0, true);
    return () => {
      document.removeEventListener("click", handleDocumentClick_0, true);
    };
  }, [activeTourId, advanceFromUserAction, run, stepIndex, steps]);
  useEffect(function completeTourWhenWatchedTargetsClicked() {
    if (!run || !activeTourId) {
      return;
    }
    const stepData_2 = getStepData({
      step: steps[stepIndex]
    });
    const completeWhenTargetsClicked = stepData_2.completeWhenTargetsClicked;
    if (!completeWhenTargetsClicked?.length) {
      return;
    }
    const watchedSelectors = completeWhenTargetsClicked;
    const tourId_1 = activeTourId;
    const forStepIndex_1 = stepIndex;
    function handleDocumentClick_1(event_1: MouseEvent) {
      const eventTarget_1 = event_1.target;
      if (!(eventTarget_1 instanceof Element)) {
        return;
      }
      const matched_0 = watchedSelectors.some(selector => eventTarget_1.closest(selector));
      if (!matched_0) {
        return;
      }
      if (advancedForStepRef.current === forStepIndex_1) {
        return;
      }
      advancedForStepRef.current = forStepIndex_1;
      window.setTimeout(() => {
        finalizeTourEnd({
          reason: "complete",
          tourId: tourId_1
        });
      }, 0);
    }
    document.addEventListener("click", handleDocumentClick_1);
    return () => {
      document.removeEventListener("click", handleDocumentClick_1);
    };
  }, [activeTourId, finalizeTourEnd, run, stepIndex, steps]);
  const abandonTour = useCallback(() => {
    if (!activeTourId || !run) {
      return;
    }
    finalizeTourEnd({
      reason: "skip",
      tourId: activeTourId
    });
  }, [activeTourId, finalizeTourEnd, run]);
  const notifyTourHostDismissed = useCallback(({
    overlayGroup: overlayGroup_0,
    reason: reason_1 = "skip"
  }: {
    overlayGroup: string;
    reason?: TourEndReason;
  }) => {
    if (!activeTourId || !run) {
      return;
    }
    const currentGroup = getStepData({
      step: steps[stepIndex]
    }).overlayGroup;
    if (currentGroup !== overlayGroup_0) {
      return;
    }
    finalizeTourEnd({
      reason: reason_1,
      tourId: activeTourId
    });
  }, [activeTourId, finalizeTourEnd, run, stepIndex, steps]);
  const baseJoyrideEventHandler = useMemo(() => createTourJoyrideEventHandler({
    activeTourId,
    onTourComplete: ({
      tourId: tourId_2
    }) => {
      finalizeTourEnd({
        reason: "complete",
        tourId: tourId_2
      });
    },
    onTourSkip: ({
      tourId: tourId_3
    }) => {
      finalizeTourEnd({
        reason: "skip",
        tourId: tourId_3
      });
    },
    setRun,
    setStepIndex: setTourStepIndex,
    shouldDeferStepAdvance: ({
      index
    }) => {
      const stepData_3 = getStepData({
        step: steps[index]
      });
      const advanceOn_1 = resolveTourAdvanceOn({
        advanceOn: stepData_3.advanceOn,
        advanceWhenTarget: stepData_3.advanceWhenTarget
      });
      return advanceOn_1 === "target-visible";
    },
    steps
  }), [activeTourId, finalizeTourEnd, setTourStepIndex, steps]);
  const handleJoyrideEvent = useCallback((data: Parameters<typeof baseJoyrideEventHandler>[0], controls: Controls) => {
    joyrideControlsRef.current = controls;
    baseJoyrideEventHandler(data, controls);
  }, [baseJoyrideEventHandler]);
  const startTour = useCallback(({
    tourId: tourId_4,
    source,
    framing = "default"
  }: StartTourRequest) => {
    if (!workspaceId) {
      return;
    }
    const action_1 = buildStartTourAction({
      activeViewNames,
      currentPathname: pathname,
      framing,
      tourId: tourId_4,
      workspaceId
    });
    if (!action_1) {
      return;
    }
    void source;
    if (action_1.type === "unavailable") {
      launchTourAction(action_1);
      return;
    }
    if (run && activeTourId !== null) {
      stopTourRun();
      setPendingLaunch(null);
      setPendingRoute(null);
    }
    if (tourId_4 === "invite-team-member") {
      closeInviteMembersSheet();
    }
    if (tourId_4 === "add-data-provider-settings" || tourId_4 === "add-data-provider-guard") {
      closeCreateDataProviderDialog();
    }
    launchTourAction(action_1);
  }, [activeTourId, activeViewNames, launchTourAction, pathname, run, stopTourRun, workspaceId]);
  const isTourActive = run && activeTourId !== null;
  return {
    abandonTour,
    activeTourId,
    executeLaunch,
    handleJoyrideEvent,
    isTourActive,
    notifyTourHostDismissed,
    run,
    setShareViewTourUnavailableOpen,
    shareViewTourUnavailableOpen,
    startTour,
    stepIndex,
    steps
  };
}